import axios from "axios";
import Cookies from "js-cookie";

export const api = axios.create({
  baseURL: '/api/',
})

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean
  }
}

function isTokenExpiringSoon(token: string, thresholdSeconds = 120): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const expiresAt = payload.exp * 1000
    return Date.now() > expiresAt - thresholdSeconds * 1000
  } catch {
    return true
  }
}

let isRefreshing = false
/** Bloque tout ré-écriture de cookies pendant / après une déconnexion. */
let isLoggingOut = false
/* eslint-disable  @typescript-eslint/no-explicit-any */
let failedQueue: any[] = []

/* eslint-disable  @typescript-eslint/no-explicit-any */
function processQueue(error: any, token: string | null = null) {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

function clearAuthCookiesClient() {
  Cookies.remove('token_auth', { path: '/' })
  Cookies.remove('refresh_auth', { path: '/' })
  Cookies.remove('user_auth', { path: '/' })
  delete api.defaults.headers.Authorization
}

/** À appeler au début du logout pour empêcher un refresh concurrent de réécrire la session. */
export function beginLogout() {
  isLoggingOut = true
  processQueue(new Error('Logout in progress'), null)
  clearAuthCookiesClient()
}

async function doRefresh(): Promise<string> {
  if (isLoggingOut) throw new Error('Logout in progress')

  const refresh = Cookies.get('refresh_auth')
  if (!refresh) throw new Error('No refresh token')

  const res = await axios.post('/api/auth/refresh/', { refresh_token: refresh })
  // Un logout a pu démarrer pendant l'attente du refresh : ne pas réécrire les cookies.
  if (isLoggingOut) {
    clearAuthCookiesClient()
    throw new Error('Logout in progress')
  }
  const { access_token, refresh_token } = res.data
  Cookies.set('token_auth', access_token, { expires: 7 })
  Cookies.set('refresh_auth', refresh_token, { expires: 7 })
  api.defaults.headers.Authorization = `Token ${access_token}`
  return access_token
}

/**
 * Returns a fresh access token, refreshing proactively if needed.
 * Exported for use with native fetch calls that can't use the Axios interceptor
 * (e.g. streaming, blob downloads).
 */
export async function ensureFreshToken(): Promise<string | null> {
  if (isLoggingOut) return null

  const token = Cookies.get('token_auth')
  const refresh = Cookies.get('refresh_auth')

  if (!token || !refresh) return token || null
  if (!isTokenExpiringSoon(token)) return token

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true
  try {
    const newToken = await doRefresh()
    processQueue(null, newToken)
    return newToken
  } catch (err) {
    processQueue(err, null)
    if (!isLoggingOut) clearAuthCookiesClient()
    return null
  } finally {
    isRefreshing = false
  }
}

// Proactive refresh: before each request, check if the token is about to expire
api.interceptors.request.use(
  async (config) => {
    const skipRefresh =
      (config as { skipAuthRefresh?: boolean }).skipAuthRefresh === true ||
      (typeof config.url === 'string' && config.url.includes('auth/logout'))

    let token: string | null = null
    if (skipRefresh) {
      token = Cookies.get('token_auth') || null
    } else {
      token = await ensureFreshToken()
      if (!token) {
        token = Cookies.get('token_auth') || null
      }
    }

    if (token) {
      config.headers.Authorization = `Token ${token}`
    }

    // If sending FormData, let the browser set the correct multipart boundary
    try {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      const maybeFormData: any = (config as any).data
      if (typeof FormData !== 'undefined' && maybeFormData instanceof FormData) {
        if (config.headers) {
          delete (config.headers as any)['Content-Type']
          delete (config.headers as any)['content-type']
        }
      }
    } catch { }

    return config
  },
  (error: Error) => Promise.reject(error)
)

// Reactive refresh: retry on 401 (expired token that slipped through)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    const isLogoutRequest =
      originalRequest?.skipAuthRefresh === true ||
      (typeof originalRequest?.url === 'string' &&
        originalRequest.url.includes('auth/logout'))

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isLogoutRequest &&
      !isLoggingOut &&
      Cookies.get('refresh_auth')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          /* eslint-disable  @typescript-eslint/no-explicit-any */
          .then((token: any) => {
            originalRequest.headers['Authorization'] = "Token " + token
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await doRefresh()
        processQueue(null, newToken)
        originalRequest.headers['Authorization'] = `Token ${newToken}`
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        if (!isLoggingOut) clearAuthCookiesClient()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)


